import { useState } from "react";
import { RiskScenarioRow } from "./RiskAssessmentsOverview.types";

// Bulk-mitigation selection for the assessment overview. Each row already is a risk assessment (row.id is the
// head id), so selection is keyed by row id — simpler than the operational overview's entity composite key.
// "Select all" and the header checkbox act on whichever eligible rows are passed in (the mitigatable, filtered
// set), so they stay consistent with what the table currently shows.
export function useBulkMitigationSelection() {
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selected, setSelected] = useState<Map<string, RiskScenarioRow>>(new Map());

    const enterSelectionMode = () => setIsSelectionMode(true);
    const exitSelectionMode = () => {
        setIsSelectionMode(false);
        setSelected(new Map());
    };

    const isSelected = (row: RiskScenarioRow) => selected.has(row.id);

    const toggle = (row: RiskScenarioRow) => {
        setSelected((current) => {
            const next = new Map(current);
            if (next.has(row.id)) next.delete(row.id);
            else next.set(row.id, row);
            return next;
        });
    };

    const removeSelected = (id: string) => {
        setSelected((current) => {
            const next = new Map(current);
            next.delete(id);
            return next;
        });
    };

    const onSelectAllChanged = (eligibleRows: Array<RiskScenarioRow>) => {
        setSelected((current) => {
            const next = new Map(current);
            const allSelected = eligibleRows.every((row) => next.has(row.id));
            eligibleRows.forEach((row) => (allSelected ? next.delete(row.id) : next.set(row.id, row)));
            return next;
        });
    };

    const getHeaderCheckBoxState = (eligibleRows: Array<RiskScenarioRow>): "true" | "false" | "indeterminate" => {
        const selectedCount = eligibleRows.filter((row) => selected.has(row.id)).length;
        if (selectedCount === 0) return "false";
        return selectedCount === eligibleRows.length ? "true" : "indeterminate";
    };

    return {
        isSelectionMode,
        enterSelectionMode,
        exitSelectionMode,
        isSelected,
        toggle,
        removeSelected,
        onSelectAllChanged,
        getHeaderCheckBoxState,
        selectedScenarios: Array.from(selected.values()),
        selectedCount: selected.size,
        isDialogOpen,
        openDialog: () => setIsDialogOpen(true),
        closeDialog: () => setIsDialogOpen(false),
    };
}

export type BulkMitigationSelection = ReturnType<typeof useBulkMitigationSelection>;
